import { useMemo } from "react";
import { object } from "prop-types";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { BreakdownTableDepths } from "@repo/utils";
import { useEstimateStructure } from "@tools";
import { TooltipIcon } from "@components";
import { Badge, Divider } from "@material-ui/core";
import { Warning } from "@assets";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/styles";

export const PriceSumAlerts = observer(({
  section
}) => {
  const { t } = useTranslation();
  const structure = useEstimateStructure();
  const classes = useStyle();
  
  const { hiddenTaskWorkTypesWithValues, lowDepthRoundingLevel } = section;
  const { showPriceFromHiddenWorkTypes } = structure.settings;
  
  const alerts = useMemo(() => {
    const list = [];
    
    // if(hasChildrenHours)
    //   list.push({ title: "alerts.editor.fixed_price_alert" });
    
    if(showPriceFromHiddenWorkTypes && hiddenTaskWorkTypesWithValues.length > 0)
      list.push({
        title: t("views.editor.hidden_work_type_price_alert.title"),
        lines: [
          t(
            `views.editor.hidden_work_type_price_alert.${hiddenTaskWorkTypesWithValues.length === 1 ? "line1_alt" : "line1"}`,
            { workTypes: hiddenTaskWorkTypesWithValues.map(wT => wT.name).join(", ") }
          ),
          t("views.editor.hidden_work_type_price_alert.line2"),
        ]
      });
    
    if(typeof lowDepthRoundingLevel === "number" && lowDepthRoundingLevel < BreakdownTableDepths.TASK) {
      const level = t(`proposal_parts.modules_levels.${lowDepthRoundingLevel}`);
      const lowerLevel = t(`proposal_parts.modules_levels.${lowDepthRoundingLevel+1}`);
      const levels = [...new Array(BreakdownTableDepths.TASK - lowDepthRoundingLevel).keys()]
        .map((i) => t(`proposal_parts.modules_levels.${lowDepthRoundingLevel + i + 1}`))
        .join(", ");
      
      list.push({
        title: t("views.editor.low_depth_rounding_alert.title", { level }),
        lines: [
          t("views.editor.low_depth_rounding_alert.text", { level, lowerLevel, levels }),
        ]
      });
    }
    
    return list;
  }, [ showPriceFromHiddenWorkTypes, hiddenTaskWorkTypesWithValues, lowDepthRoundingLevel ])
  
  if(!alerts.length)
    return <></>;
  
  return (
    <Badge
      color="primary"
      badgeContent={alerts.length}
      invisible={alerts.length <= 1}
      overlap="circular"
      max={9}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      className="noDrag"
      classes={{
        badge: classes.badge
      }}
    >
      <TooltipIcon
        className="text-sm"
        icon={ <Warning className="color-warning"/> }
      >
        <Grid item container direction="column">
          {
            alerts.map((a, i, l) => (
              <>
                <p className="text-sm text-sm font-bold">
                  { a.title }
                </p>
                {
                  a.lines.map(line => (<p key={line} className="text-sm mt-2">{ line }</p>))
                }
                {
                  i < l.length-1 &&
                  <Divider className="bg-secondary my-2 transparent-1" />
                }
              </>
            ))
          }
        </Grid>
      </TooltipIcon>
    </Badge>
  );
})

PriceSumAlerts.propTypes = {
  section: object.isRequired
}

const useStyle = makeStyles(() => ({
  badge: {
    top: "24%",
    right: "24%",
    width:  10,
    minWidth: 10,
    height: 10,
    fontSize: 10,
  }
}));